import { Injectable, inject } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, RedirectRequest } from '@azure/msal-browser';
import { Subject } from 'rxjs';

import { Router } from '@angular/router';
import { AUTH_TOKEN, REDIRECT_PATH } from '@shared/consts';
import { TenantStore } from '@signal-store/tenant.store';
import { AzureADB2C, AzureADB2CConstant } from 'src/environments/aadb2c';
import { EnvironmentConfigService } from './environment-config.service';
import { MSALScopes } from './msal-factory';

@Injectable({
  providedIn: 'root',
})
export class MsalAuthService {
  private router = inject(Router);
  private tenantStore = inject(TenantStore);
  private environmentConfigService = inject(EnvironmentConfigService);
  private msalService = inject(MsalService);
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);

  private readonly _destroying$ = new Subject<void>();
  private aadb2c: AzureADB2CConstant = new AzureADB2C().generateConstants()[window.location.host];

  async loginRedirect() {
    // verify if there is a valid token
    // if there is no valid token, redirect to login
    // if there is a valid token, redirect to the redirect path or tenant list
    await this.verifyLoginToken(
      (result: AuthenticationResult) => this.handleSuccessfulLogin(result),
      async () => await this.handleRedirect(),
    );
  }

  async logout(keepRedirect: boolean = false) {
    await this.msalService.instance.initialize();

    // verify if we have a redirect
    const redirectPath = localStorage.getItem(REDIRECT_PATH);

    // clear storage
    localStorage.clear();

    // restore redirect if any
    if (keepRedirect && redirectPath) {
      localStorage.setItem(REDIRECT_PATH, redirectPath);
    }

    try {
      const account = this.msalService.instance.getAllAccounts()[0];
      this.msalService
        .logoutRedirect({
          account: account,
          extraQueryParameters: {
            client_id: this.aadb2c.CLIENT_ID,
          },
          idTokenHint: account?.idToken,
          postLogoutRedirectUri: '/login',
        })
        .subscribe();
    } catch (error) {
      console.error(error);
    }
  }

  async verifyLoginToken(onSuccess: (result: AuthenticationResult) => void, onFailure: () => void) {
    await this.msalService.instance.initialize();

    // Rehydrate the active account if not already set
    let account = this.msalService.instance.getActiveAccount();
    if (!account) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        account = accounts[0];
        this.msalService.instance.setActiveAccount(account);
      }
    }

    if (account) {
      // Attempt to acquire a token silently
      this.msalService
        .acquireTokenSilent({
          scopes: MSALScopes,
          account: account,
        })
        .subscribe({
          next: (result: AuthenticationResult) => onSuccess(result),

          // Token is invalid or expired, proceed with login
          error: () => onFailure(),
        });
    } else {
      // No active account, proceed with login
      onFailure();
    }
  }

  handleSuccessfulLogin(result: AuthenticationResult) {
    // store the auth token in local storage
    localStorage.setItem(AUTH_TOKEN, result.idToken);
    this.environmentConfigService.loadConfig();

    // redirect to the path before login if any
    const redirectPath = localStorage.getItem(REDIRECT_PATH);
    if (redirectPath) {
      localStorage.removeItem(REDIRECT_PATH);
      window.location.href = redirectPath;
    } else {
      // load the tenant list
      this.tenantStore.loadTenants().subscribe(_ => {
        // navigate to the tenant list
        this.router.navigate(['/tenant-list']);
      });
    }
  }

  async handleRedirect() {
    await this.msalService.instance.initialize();
    if (this.msalGuardConfig.authRequest) {
      this.msalService
        .loginRedirect({
          ...this.msalGuardConfig.authRequest,
          redirectStartPage: this.aadb2c.REDIRECT_PATH,
          redirectUri: this.aadb2c.REDIRECT_PATH,
        } as RedirectRequest)
        .subscribe();
    } else {
      this.msalService.loginRedirect().subscribe();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
